import { TFormFieldName } from '@infotech-soe/metaschema';
import { produce } from 'immer';
import isEmpty from 'lodash/isEmpty';

import {
  DatasetConfig,
  DatasetPresetRule,
  IFrontDatasetPreset,
  TotalsRuleFront,
  TToolbarConfig,
} from '../../../../models/user-config/DisplayModeConfig';
import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';

import { getOrderedColumns } from './getOrderedColumns';
import {
  copyPreset,
  createPreset,
  deletePreset,
  exportPreset,
  renamePreset,
  resetPreset,
} from './preset';

interface PresetActionConfirmation {
  presetName: keyof DatasetConfig['preset'];
  handler: (name: string) => unknown;
}

interface ConfigFormState {
  config: DatasetConfig;
  presetActionConfirmation: PresetActionConfirmation;
  isFormPresetOpened: boolean;
  order: DatasetGridColumn[];
  shouldUpdateDatasetOnSave: boolean;
  totalsField?: TFormFieldName;
}

export const enum ConfigFormActionType {
  ADD_PRESET,
  COPY_PRESET,
  DELETE_PRESET,
  RENAME_PRESET,
  RESET_PRESET,
  IMPORT_PRESET,
  EXPORT_PRESET,
  UPDATE_CONFIG_PRESET_RULE,
  UPDATE_CONFIG,
  UPDATE_CONFIG_PRESET,
  UPDATE_CONFIG_PRESET_TOOLBAR_COMMANDS,
  SET_ORDER,
  SET_PRESET_ACTION_CONFIRMATION,
  SET_IS_FORM_PRESET_OPENED,
  SET_SHOULD_UPDATE_DATASET_ON_SAVE,
  SET_TOTALS_FIELD,
  SET_TOTALS_CONFIG,
}

type AddPresetAction = [
  type: ConfigFormActionType.ADD_PRESET,
  payload: { name: string; orderedColumns: DatasetGridColumn[] },
];
type CopyPresetAction = [type: ConfigFormActionType.COPY_PRESET, name: string];
type DeletePresetAction = [type: ConfigFormActionType.DELETE_PRESET];
type RenamePresetAction = [
  type: ConfigFormActionType.RENAME_PRESET,
  name: string,
];
type ResetPresetAction = [
  type: ConfigFormActionType.RESET_PRESET,
  columns: DatasetGridColumn[],
];
type ImportPresetAction = [
  type: ConfigFormActionType.IMPORT_PRESET,
  payload: DatasetConfig,
];
type ExportPresetAction = [type: ConfigFormActionType.EXPORT_PRESET];
type UpdateConfigPresetRuleAction = [
  type: ConfigFormActionType.UPDATE_CONFIG_PRESET_RULE,
  payload: { ruleName: string; rule: DatasetPresetRule },
];
type UpdateConfigAction = [
  type: ConfigFormActionType.UPDATE_CONFIG,
  // TODO: type this better, cause ow you can pass undefined in required field
  payload: { [key in keyof DatasetConfig]?: DatasetConfig[key] },
];
type UpdateConfigPresetAction = [
  type: ConfigFormActionType.UPDATE_CONFIG_PRESET,
  payload: {
    // TODO: type this better, cause ow you can pass undefined in required field
    [key in keyof IFrontDatasetPreset]?: IFrontDatasetPreset[key];
  },
];
type UpdateConfigPresetToolbarCommandsAction = [
  type: ConfigFormActionType.UPDATE_CONFIG_PRESET_TOOLBAR_COMMANDS,
  payload: { commandName: string; value: TToolbarConfig },
];
type SetOrderAction = [
  type: ConfigFormActionType.SET_ORDER,
  payload: DatasetGridColumn[],
];
type SetPresetActionConfirmationAction = [
  type: ConfigFormActionType.SET_PRESET_ACTION_CONFIRMATION,
  payload: PresetActionConfirmation,
];
type SetIsFormPresetOpenedAction = [
  type: ConfigFormActionType.SET_IS_FORM_PRESET_OPENED,
  payload: boolean,
];
type SetShouldUpdateDatasetOnSaveAction = [
  type: ConfigFormActionType.SET_SHOULD_UPDATE_DATASET_ON_SAVE,
  payload: boolean,
];
type SetTotalsFieldAction = [
  type: ConfigFormActionType.SET_TOTALS_FIELD,
  payload?: TFormFieldName,
];
type SetTotalsConfigAction = [
  type: ConfigFormActionType.SET_TOTALS_CONFIG,
  payload: {
    field: TFormFieldName;
    modifiedTotals?: TotalsRuleFront[];
    shouldUpdateDatasetOnSave?: boolean;
  },
];

type ConfigFormAction =
  | AddPresetAction
  | CopyPresetAction
  | DeletePresetAction
  | RenamePresetAction
  | ImportPresetAction
  | ExportPresetAction
  | ResetPresetAction
  | UpdateConfigPresetRuleAction
  | UpdateConfigAction
  | UpdateConfigPresetAction
  | UpdateConfigPresetToolbarCommandsAction
  | SetOrderAction
  | SetPresetActionConfirmationAction
  | SetIsFormPresetOpenedAction
  | SetShouldUpdateDatasetOnSaveAction
  | SetTotalsFieldAction
  | SetTotalsConfigAction;

const configFormReducer = (
  state: ConfigFormState,
  action: ConfigFormAction
): ConfigFormState =>
  produce(state, draft => {
    switch (action[0]) {
      case ConfigFormActionType.UPDATE_CONFIG:
        draft.config = { ...draft.config, ...action[1] };
        break;
      case ConfigFormActionType.UPDATE_CONFIG_PRESET: {
        const [, field] = action;
        const { preset, currentPreset } = draft.config;
        preset[currentPreset] = { ...preset[currentPreset], ...field };
        break;
      }
      case ConfigFormActionType.UPDATE_CONFIG_PRESET_TOOLBAR_COMMANDS: {
        const [, { commandName, value }] = action;
        const { preset, currentPreset } = draft.config;
        const selectedPreset = preset[currentPreset];
        const existing = selectedPreset.toolbarCommands?.[commandName] ?? {};
        // Migrate legacy 'hide' to separate hideInToolbar/hideInMenu
        const legacyHide = existing.hide ?? false;
        const migratedExisting: TToolbarConfig = {
          hideInToolbar: existing.hideInToolbar ?? legacyHide,
          hideInMenu: existing.hideInMenu ?? legacyHide,
        };
        const updated: TToolbarConfig = { ...migratedExisting, ...value };
        if (updated.hideInToolbar || updated.hideInMenu) {
          selectedPreset.toolbarCommands = {
            ...selectedPreset.toolbarCommands,
            [commandName]: updated,
          };
        } else {
          const { toolbarCommands } = selectedPreset;
          delete toolbarCommands?.[commandName];
          if (isEmpty(toolbarCommands)) {
            delete selectedPreset.toolbarCommands;
          }
        }
        break;
      }
      case ConfigFormActionType.SET_ORDER:
        draft.order = action[1];
        break;
      case ConfigFormActionType.ADD_PRESET: {
        const { name, orderedColumns } = action[1];
        draft.config = createPreset(draft.config, orderedColumns, name);
        draft.isFormPresetOpened = false;
        break;
      }
      case ConfigFormActionType.COPY_PRESET:
        draft.config = copyPreset(draft.config, action[1]);
        draft.isFormPresetOpened = false;
        break;
      case ConfigFormActionType.DELETE_PRESET:
        draft.config = deletePreset(draft.config, draft.config.currentPreset);
        draft.isFormPresetOpened = false;
        break;
      case ConfigFormActionType.RENAME_PRESET:
        draft.config = renamePreset(draft.config, action[1]);
        draft.isFormPresetOpened = false;
        break;
      case ConfigFormActionType.IMPORT_PRESET:
        draft.config = action[1];
        draft.isFormPresetOpened = false;
        break;
      case ConfigFormActionType.EXPORT_PRESET: {
        const { currentPreset } = draft.config;
        exportPreset(currentPreset, draft.config.preset[currentPreset]);
        break;
      }
      case ConfigFormActionType.RESET_PRESET: {
        const [, columns] = action;
        draft.config = resetPreset(draft.config, columns);
        draft.order = getOrderedColumns(draft.config.columnsOrder, columns);
        draft.isFormPresetOpened = false;
        break;
      }
      case ConfigFormActionType.UPDATE_CONFIG_PRESET_RULE: {
        const [, { rule, ruleName }] = action;
        const { preset, currentPreset } = draft.config;
        preset[currentPreset].rules[ruleName] = rule;
        break;
      }
      case ConfigFormActionType.SET_PRESET_ACTION_CONFIRMATION:
        draft.presetActionConfirmation = action[1];
        draft.isFormPresetOpened = true;
        break;
      case ConfigFormActionType.SET_IS_FORM_PRESET_OPENED:
        draft.isFormPresetOpened = action[1];
        break;
      case ConfigFormActionType.SET_SHOULD_UPDATE_DATASET_ON_SAVE:
        draft.shouldUpdateDatasetOnSave = action[1];
        break;
      case ConfigFormActionType.SET_TOTALS_FIELD:
        draft.totalsField = action[1];
        break;
      case ConfigFormActionType.SET_TOTALS_CONFIG: {
        const { field, modifiedTotals, shouldUpdateDatasetOnSave } = action[1];
        const { preset, currentPreset } = draft.config;
        if (modifiedTotals?.length) {
          preset[currentPreset].totals = {
            ...preset[currentPreset].totals,
            [field]: modifiedTotals,
          };
        } else {
          const { totals } = preset[currentPreset];
          delete totals?.[field];
          if (isEmpty(totals)) {
            delete preset[currentPreset].totals;
          }
        }
        draft.shouldUpdateDatasetOnSave ||= !!shouldUpdateDatasetOnSave;
        draft.totalsField = undefined;
        break;
      }
      default:
        throw new Error(`Action handler ${action[0]} is not implemented`);
    }
  });

export default configFormReducer;
